import React, { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUploadField from "@/components/shared/ImageUploadField";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EMPTY = {
    // New required fields
    first_name_en: "",
    first_name_ar: "",
    last_name_en: "",
    last_name_ar: "",
    title_en: "",
    title_ar: "",
    gender: "",
    primary_specialty: "",
    // Existing fields (keep for compatibility)
    full_name_en: "",
    full_name_ar: "",
    role_en: "",
    role_ar: "",
    license_number: "",
    qualifications: "",
    photo_url: "",
    availability: [],
    is_active: true,
};

/**
 * Shared bilingual staff create/edit dialog with availability matrix.
 * Consumers pass `save({ id, payload })` returning a Promise<savedStaff>.
 */
export default function StaffFormDialog({ open, onOpenChange, initial, locations, save, onSaved }) {
    const { t, isRTL } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        if (initial?.id) {
            setForm({
                ...EMPTY, ...initial,
                availability: (initial.availability || []).map((s) => ({ ...s })),
            });
        } else {
            setForm(EMPTY);
        }
        setError("");
    }, [open, initial]);

    const locationsById = useMemo(
        () => Object.fromEntries((locations || []).map((l) => [l.id, l])),
        [locations],
    );

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const addSlot = () => {
        if (!locations || locations.length === 0) {
            setError(t("provider.staff.errors.needLocation"));
            return;
        }
        update({
            availability: [
                ...form.availability,
                { location_id: locations[0].id, days: [], from_time: "10:00", to_time: "18:00" },
            ],
        });
    };

    const updateSlot = (idx, patch) => {
        const next = [...form.availability];
        next[idx] = { ...next[idx], ...patch };
        update({ availability: next });
    };

    const removeSlot = (idx) => {
        const next = [...form.availability];
        next.splice(idx, 1);
        update({ availability: next });
    };

    const toggleDay = (idx, day) => {
        const slot = form.availability[idx];
        const days = slot.days.includes(day) ? slot.days.filter((d) => d !== day) : [...slot.days, day];
        updateSlot(idx, { days });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSaving(true);
        try {
            const payload = {
                ...form,
                role_en: form.role_en || null,
                role_ar: form.role_ar || null,
                license_number: form.license_number || null,
                qualifications: form.qualifications || null,
                photo_url: form.photo_url || null,
            };
            const saved = await save({ id: initial?.id || null, payload });
            toast.success(t(initial?.id ? "provider.staff.updated" : "provider.staff.created"));
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl"
                data-testid="staff-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t(initial?.id ? "provider.staff.editTitle" : "provider.staff.addTitle")}</DialogTitle>
                    <DialogDescription>{t("provider.staff.dialogDescription")}</DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4 items-start">
                        <ImageUploadField
                            value={form.photo_url}
                            onChange={(v) => update({ photo_url: v })}
                            shape="square"
                            maxDim={600}
                            testIdPrefix="staff-photo"
                            label={t("provider.staff.fields.photoLabel")}
                        />
                        <div className="space-y-3">
                            {/* First Name & Last Name */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("provider.staff.fields.firstNameEn")} <span className="text-red-500">*</span></Label>
                                    <Input dir="ltr" required value={form.first_name_en} onChange={(e) => update({ first_name_en: e.target.value, full_name_en: `${e.target.value} ${form.last_name_en}`.trim() })} className="mt-1.5" placeholder="Mohamed" data-testid="staff-firstname-en" />
                                </div>
                                <div>
                                    <Label>{t("provider.staff.fields.lastNameEn")} <span className="text-red-500">*</span></Label>
                                    <Input dir="ltr" required value={form.last_name_en} onChange={(e) => update({ last_name_en: e.target.value, full_name_en: `${form.first_name_en} ${e.target.value}`.trim() })} className="mt-1.5" placeholder="Ali" data-testid="staff-lastname-en" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("provider.staff.fields.firstNameAr")} <span className="text-red-500">*</span></Label>
                                    <Input dir="rtl" lang="ar" required value={form.first_name_ar} onChange={(e) => update({ first_name_ar: e.target.value, full_name_ar: `${e.target.value} ${form.last_name_ar}`.trim() })} className="mt-1.5 font-arabic" placeholder="محمد" data-testid="staff-firstname-ar" />
                                </div>
                                <div>
                                    <Label>{t("provider.staff.fields.lastNameAr")} <span className="text-red-500">*</span></Label>
                                    <Input dir="rtl" lang="ar" required value={form.last_name_ar} onChange={(e) => update({ last_name_ar: e.target.value, full_name_ar: `${form.first_name_ar} ${e.target.value}`.trim() })} className="mt-1.5 font-arabic" placeholder="علي" data-testid="staff-lastname-ar" />
                                </div>
                            </div>

                            {/* Professional Title (Dr., Prof., etc.) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("provider.staff.fields.titleEn")} <span className="text-red-500">*</span></Label>
                                    <Input dir="ltr" required value={form.title_en} onChange={(e) => update({ title_en: e.target.value })} className="mt-1.5" placeholder="Dr." data-testid="staff-title-en" />
                                    <p className="text-xs text-muted-foreground mt-1">{t("provider.staff.fields.titleHint")}</p>
                                </div>
                                <div>
                                    <Label>{t("provider.staff.fields.titleAr")} <span className="text-red-500">*</span></Label>
                                    <Input dir="rtl" lang="ar" required value={form.title_ar} onChange={(e) => update({ title_ar: e.target.value })} className="mt-1.5 font-arabic" placeholder="د." data-testid="staff-title-ar" />
                                </div>
                            </div>

                            {/* Job Title/Role (Chief Surgeon, etc.) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("provider.staff.fields.jobTitleEn")}</Label>
                                    <Input dir="ltr" value={form.role_en || ""} onChange={(e) => update({ role_en: e.target.value })} className="mt-1.5" placeholder="Chief Surgeon for Kidney and Liver Disease" data-testid="staff-role-en" />
                                    <p className="text-xs text-muted-foreground mt-1">{t("provider.staff.fields.jobTitleHint")}</p>
                                </div>
                                <div>
                                    <Label>{t("provider.staff.fields.jobTitleAr")}</Label>
                                    <Input dir="rtl" lang="ar" value={form.role_ar || ""} onChange={(e) => update({ role_ar: e.target.value })} className="mt-1.5 font-arabic" placeholder="جراح رئيسي للكلى والكبد" data-testid="staff-role-ar" />
                                </div>
                            </div>

                            {/* Gender */}
                            <div>
                                <Label>{t("provider.staff.fields.gender")} <span className="text-red-500">*</span></Label>
                                <Select value={form.gender} onValueChange={(v) => update({ gender: v })} required>
                                    <SelectTrigger className="mt-1.5">
                                        <SelectValue placeholder={t("provider.staff.fields.selectGender")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="male">{t("provider.staff.fields.male")}</SelectItem>
                                        <SelectItem value="female">{t("provider.staff.fields.female")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label>{t("provider.staff.fields.license")}</Label>
                        <Input value={form.license_number || ""} onChange={(e) => update({ license_number: e.target.value })} className="mt-1.5" data-testid="staff-license" />
                    </div>
                    <div>
                        <Label>{t("provider.staff.fields.qualifications")}</Label>
                        <Textarea rows={2} value={form.qualifications || ""} onChange={(e) => update({ qualifications: e.target.value })} className="mt-1.5" data-testid="staff-qualifications" />
                    </div>

                    <div className="rounded-xl border border-border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-base">{t("provider.staff.availability.title")}</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">{t("provider.staff.availability.subtitle")}</p>
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={addSlot} data-testid="staff-add-slot">
                                <Plus className="h-4 w-4 me-1" />
                                {t("provider.staff.availability.addSlot")}
                            </Button>
                        </div>

                        {form.availability.length === 0 && (
                            <p className="text-sm text-muted-foreground py-4 text-center">{t("provider.staff.availability.empty")}</p>
                        )}

                        {form.availability.map((slot, idx) => (
                            <div key={idx} className="rounded-xl border border-border bg-muted/30 p-3 space-y-3" data-testid={`staff-slot-${idx}`}>
                                <div className="flex items-start gap-2">
                                    <Select
                                        value={slot.location_id}
                                        onValueChange={(v) => updateSlot(idx, { location_id: v })}
                                    >
                                        <SelectTrigger className="flex-1" data-testid={`staff-slot-location-${idx}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(locations || []).map((l) => (
                                                <SelectItem key={l.id} value={l.id}>{l.name_en}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button type="button" size="icon" variant="ghost" onClick={() => removeSlot(idx)} data-testid={`staff-slot-remove-${idx}`} aria-label="Remove">
                                        <X className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {DAYS.map((d) => (
                                        <label
                                            key={d}
                                            className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer border ${slot.days.includes(d) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"}`}
                                            data-testid={`staff-slot-day-${idx}-${d}`}
                                            onClick={() => toggleDay(idx, d)}
                                        >
                                            <Checkbox
                                                checked={slot.days.includes(d)}
                                                onCheckedChange={() => toggleDay(idx, d)}
                                                className="hidden"
                                            />
                                            {t(`provider.staff.days.${d}`)}
                                        </label>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs">{t("provider.staff.fields.from")}</Label>
                                        <Input type="time" value={slot.from_time || ""} onChange={(e) => updateSlot(idx, { from_time: e.target.value })} className="mt-1" data-testid={`staff-slot-from-${idx}`} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">{t("provider.staff.fields.to")}</Label>
                                        <Input type="time" value={slot.to_time || ""} onChange={(e) => updateSlot(idx, { to_time: e.target.value })} className="mt-1" data-testid={`staff-slot-to-${idx}`} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-border p-3">
                        <Label>{t("provider.staff.fields.active")}</Label>
                        <Switch checked={form.is_active} onCheckedChange={(v) => update({ is_active: v })} data-testid="staff-active" />
                    </div>

                    {error && <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="staff-form-error">{error}</div>}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="staff-cancel">{t("common.cancel")}</Button>
                        <Button type="submit" disabled={saving} data-testid="staff-save">
                            {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
